import { FormEventHandler, useEffect, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import './SearchBox.less'

export interface LocationSearchBoxProps {
  id?: string
  setLocation: (value: string) => void
  location: string
  countryRestrictions: boolean
}

interface LoadPredictionsArgs {
  search: string
  autocompleteService: google.maps.places.AutocompleteService
  setPredictions: (predictions: { label: string; value: string }[]) => void
  selectedPrediction: { label: string; value: string } | null
  countryRestrictions: boolean
}

const loadPredictions = ({
  search,
  autocompleteService,
  setPredictions,
  selectedPrediction,
  countryRestrictions,
}: LoadPredictionsArgs): void => {
  const handlePredictions = (predictions: google.maps.places.AutocompletePrediction[] | null) => {
    if (predictions === null) return

    const newPredictions = predictions.map(({ description, place_id }) => ({
      label: description,
      value: place_id,
    }))
    setPredictions(selectedPrediction ? [selectedPrediction, ...newPredictions] : newPredictions)
  }

  autocompleteService.getPlacePredictions(
    {
      input: search,
      componentRestrictions: countryRestrictions
        ? { country: ['gb', 'ie', 'nl', 'au'] }
        : undefined,
      types: ['geocode', 'establishment'],
    },
    handlePredictions
  )
}

const getPlaceholderText = (): string => {
  const browserCountry = navigator.language.slice(3, 5)
  switch (browserCountry) {
    case 'GB':
      return "Try 'M12', 'Soho', 'Bristol Temple Meads Station'..."
    case 'AU':
      return "Try '2000', 'Fortitude Valley' or 'Flinders Street Station'..."
    default:
      return 'Try entering a venue, address or district...'
  }
}

const LocationSearchBox = ({
  location,
  setLocation,
  id,
  countryRestrictions,
}: LocationSearchBoxProps): JSX.Element => {
  // spoof place id '1' avoids a GoogleAPI request for briefs where the location is already defined
  const defaultLocationOption = location !== '' ? { label: location, value: '1' } : null
  const [predictions, setPredictions] = useState<{ label: string; value: string }[]>(
    defaultLocationOption ? [defaultLocationOption] : []
  )
  const [searchStr, setSearchStr] = useState(location)
  const [selectedPrediction, setSelectedPrediction] = useState(defaultLocationOption)
  const [invalidMessage, setInvalidMessage] = useState<string | null>(null)
  useEffect(() => {
    // handle parent-initiated changes to location (eg. geocode fetches)
    setSelectedPrediction(defaultLocationOption)
  }, [location])

  const autocompleteService = new google.maps.places.AutocompleteService()
  const onSearch = (search: string) =>
    loadPredictions({
      search,
      setPredictions,
      autocompleteService,
      selectedPrediction,
      countryRestrictions,
    })
  const onInvalid: FormEventHandler<HTMLInputElement> = (event) => {
    // Mui doesn't easily expose the underlying validity state
    setInvalidMessage('Please choose a location')
    event.preventDefault()
  }

  return (
    <Autocomplete
      id={id}
      fullWidth
      filterOptions={(x) => x}
      options={predictions}
      renderOption={(props, option) => (
        <li {...props} key={option.value}>
          {option.label}
        </li>
      )}
      className='location-search-box'
      autoComplete
      includeInputInList
      filterSelectedOptions
      size='small'
      noOptionsText={searchStr ? undefined : getPlaceholderText()}
      isOptionEqualToValue={(option, value) => option.value === value?.value}
      value={selectedPrediction}
      onChange={(_event, newValue: { label: string; value: string } | null) => {
        setPredictions(newValue ? [newValue, ...predictions] : predictions)
        setLocation(newValue?.value ?? '')
        setSelectedPrediction(newValue ?? null)
      }}
      onInputChange={(_event, newInputValue) => {
        setSearchStr(newInputValue)
        onSearch(newInputValue)
        setInvalidMessage(null)
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          value={searchStr}
          label='Search for a location'
          fullWidth
          required
          error={!!invalidMessage}
          helperText={invalidMessage}
          onInvalid={onInvalid}
          InputLabelProps={{ required: false }}
        />
      )}
    />
  )
}

export default LocationSearchBox
